import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Users, Archive, Trash2, Tag, MessageSquare } from "lucide-react";

function formatContactDate(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [instanceId, setInstanceId] = useState("");
  const [includeArchived, setIncludeArchived] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const loadInstances = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/instances");
      setInstances(Array.isArray(data?.instances) ? data.instances : []);
    } catch {
      setInstances([]);
    }
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts", {
        params: {
          search,
          instance_id: instanceId || undefined,
          include_archived: includeArchived,
          limit: 1000,
        },
      });
      const rows = Array.isArray(data?.contacts) ? data.contacts : [];
      setContacts(rows);
      setSelectedIds((prev) => prev.filter((id) => rows.some((c) => c.id === id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar contatos");
      setContacts([]);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, instanceId, search]);

  useEffect(() => {
    void loadInstances();
  }, [loadInstances]);

  useEffect(() => {
    const t = setTimeout(() => {
      void loadContacts();
    }, 200);
    return () => clearTimeout(t);
  }, [loadContacts]);

  const selectedCount = selectedIds.length;
  const allVisibleSelected = useMemo(
    () => contacts.length > 0 && contacts.every((c) => selectedIds.includes(c.id)),
    [contacts, selectedIds],
  );

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(contacts.map((c) => c.id));
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const runBulk = async (action, extra = {}) => {
    if (!selectedIds.length) {
      toast.error("Selecione ao menos um contato");
      return;
    }
    try {
      const { data } = await api.post("/contacts/bulk-action", {
        action,
        contact_ids: selectedIds,
        ...extra,
      });
      const updated = Number(data?.updated || 0);
      if (action === "delete") toast.success(`${updated} contato(s) excluído(s)`);
      else if (action === "archive") toast.success(`${updated} conversa(s) arquivada(s)`);
      else if (action === "add_tag") toast.success(`Tag adicionada em ${updated} contato(s)`);
      else if (action === "remove_tag") toast.success(`Tag removida em ${updated} contato(s)`);
      setSelectedIds([]);
      await loadContacts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível concluir a ação");
    }
  };

  const confirmDeleteBulk = async () => {
    if (!selectedIds.length) return;
    const ok = window.confirm(
      `Tem certeza que deseja excluir ${selectedIds.length} contato(s)? Essa ação remove também o histórico da conversa desses contatos.`,
    );
    if (!ok) return;
    await runBulk("delete");
  };

  const promptAddTag = async () => {
    const tag = window.prompt("Digite a tag para adicionar (ex: cliente quente):", "");
    if (!tag || !tag.trim()) return;
    await runBulk("add_tag", { tag: tag.trim() });
  };

  const promptRemoveTag = async () => {
    const tag = window.prompt("Digite a tag para remover:", "");
    if (!tag || !tag.trim()) return;
    await runBulk("remove_tag", { tag: tag.trim() });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-cp-bg p-4">
        <div className="mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-cp-primary-light" />
          <h1 className="font-heading text-lg font-bold">Contatos / Clientes</h1>
        </div>
        <p className="text-sm text-slate-400">Organize seus contatos, filtre por WhatsApp e faça ações em massa.</p>
      </div>

      <div className="rounded-xl border border-white/10 bg-cp-bg p-4">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="relative md:col-span-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone"
              className="w-full rounded-lg border border-white/10 bg-cp-elevated py-2 pl-9 pr-3 text-sm outline-none focus:border-cp-primary/60"
            />
          </label>

          <select
            value={instanceId}
            onChange={(e) => setInstanceId(e.target.value)}
            className="rounded-lg border border-white/10 bg-cp-elevated px-3 py-2 text-sm outline-none focus:border-cp-primary/60"
          >
            <option value="">Todos os WhatsApps</option>
            {instances.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.display_name || inst.instance_name || "WhatsApp"}
              </option>
            ))}
          </select>

          <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-cp-elevated px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Mostrar arquivados
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-cp-bg p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggleAll}
            className="rounded-lg border border-white/10 px-3 py-1.5 text-sm hover:bg-cp-elevated"
          >
            {allVisibleSelected ? "Desmarcar todos" : "Selecionar todos"}
          </button>
          <span className="text-sm text-slate-400">{selectedCount} selecionado(s)</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void runBulk("archive")}
              disabled={!selectedCount}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-cp-elevated"
            >
              <Archive className="h-4 w-4" /> Arquivar conversas
            </button>
            <button
              type="button"
              onClick={() => void promptAddTag()}
              disabled={!selectedCount}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-cp-elevated"
            >
              <Tag className="h-4 w-4" /> Adicionar tag
            </button>
            <button
              type="button"
              onClick={() => void promptRemoveTag()}
              disabled={!selectedCount}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-cp-elevated"
            >
              <Tag className="h-4 w-4" /> Remover tag
            </button>
            <button
              type="button"
              onClick={() => void confirmDeleteBulk()}
              disabled={!selectedCount}
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm text-red-200 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" /> Excluir contatos
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-slate-400">
                <th className="px-2 py-2 w-10" />
                <th className="px-2 py-2">Nome</th>
                <th className="px-2 py-2">Telefone</th>
                <th className="px-2 py-2">WhatsApp de origem</th>
                <th className="px-2 py-2">Última mensagem</th>
                <th className="px-2 py-2">Último contato</th>
                <th className="px-2 py-2">Tags</th>
                <th className="px-2 py-2 w-28" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-2 py-6 text-center text-slate-400">
                    Carregando contatos...
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-2 py-6 text-center text-slate-400">
                    Nenhum contato encontrado.
                  </td>
                </tr>
              ) : (
                contacts.map((c) => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-cp-elevated/40">
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => toggleOne(c.id)}
                      />
                    </td>
                    <td className="px-2 py-2 font-medium text-slate-100">{c.name || "Sem nome"}</td>
                    <td className="px-2 py-2 text-slate-300">{c.phone || "-"}</td>
                    <td className="px-2 py-2 text-slate-300">
                      {c.last_message_instance_name || c.last_message_instance_id || "-"}
                    </td>
                    <td className="px-2 py-2 text-slate-300">{c.last_message_preview || "-"}</td>
                    <td className="px-2 py-2 text-slate-300">{formatContactDate(c.last_message_at || c.created_at)}</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(c.tags) ? c.tags : []).slice(0, 3).map((tag) => (
                          <span key={`${c.id}-${tag}`} className="rounded-full border border-cp-primary/40 bg-cp-primary/10 px-2 py-0.5 text-xs">
                            {tag}
                          </span>
                        ))}
                        {Array.isArray(c.tags) && c.tags.length > 3 && (
                          <span className="text-xs text-slate-400">+{c.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/app/chat?contact_id=${encodeURIComponent(c.id)}`)}
                        className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs hover:bg-cp-elevated"
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        Abrir chat
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
